// TerminalChatStream — opens useChat from the AI SDK v4 with the user's chosen
// session type. Renders incoming assistant text with a typewriter.

import { useChat } from "@ai-sdk/react";
import { useEffect, useRef, useState } from "react";
import type { SessionType } from "@shukan/shared";
import { TerminalLine } from "../terminal/TerminalLine";
import { getAccessToken } from "../../lib/supabase";

interface Props {
  session: SessionType;
  conversationId: string | null;
  onConversationIdChange: (id: string) => void;
  historyMessages: any[];
}

export function TerminalChatStream({
  session,
  conversationId,
  onConversationIdChange,
  historyMessages,
}: Props) {
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    getAccessToken().then(setToken);
  }, []);

  const { messages, input, handleInputChange, handleSubmit, isLoading, stop, setMessages, error } = useChat({
    api: `${import.meta.env.VITE_API_URL ?? ""}/api/ai/chat`,
    body: { sessionType: session, conversationId },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    streamProtocol: "data",
    onResponse: (response) => {
      const headerId = response.headers.get("x-shukan-conversation-id");
      if (headerId) {
        onConversationIdChange(headerId);
      }
    },
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [typed, setTyped] = useState("");

  // Populate loaded history messages when they change
  useEffect(() => {
    if (historyMessages && historyMessages.length > 0) {
      setMessages(
        historyMessages.map((m, idx) => ({
          id: m.id || String(idx),
          role: m.role,
          content: m.content,
        }))
      );
    } else {
      setMessages([]);
    }
  }, [historyMessages, setMessages]);

  function textOf(m: { content: unknown }): string {
    const c = m.content;
    if (typeof c === "string") return c;
    if (Array.isArray(c)) {
      return c
        .map((p) =>
          typeof p === "string"
            ? p
            : ((p as { text?: string }).text ?? ""),
        )
        .join("");
    }
    return "";
  }

  // Typewriter the most recent assistant message.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") {
      setTyped("");
      return;
    }
    const full = textOf(last);
    
    // Complete typewriter immediately if streaming has finished.
    if (!isLoading) {
      setTyped(full);
      return;
    }

    if (full.length <= typed.length) {
      setTyped(full);
      return;
    }
    let i = typed.length;
    const id = setInterval(() => {
      i += 4;
      setTyped(full.slice(0, i));
      if (i >= full.length) {
        clearInterval(id);
        setTyped(full);
      }
    }, 20);
    return () => clearInterval(id);
  }, [messages, typed.length, isLoading]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: Number.MAX_SAFE_INTEGER });
  }, [typed, messages.length]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, minHeight: 0 }}>
      {/* Messages area */}
      <div ref={scrollRef} style={{ flex: 1, overflow: "auto", paddingRight: "0.5rem" }}>
        {messages.length === 0 ? (
          <TerminalLine dim>${` ${session} session ready. type below.`}</TerminalLine>
        ) : null}
        {messages.map((m, i) => {
          const text = textOf(m);
          const isStreaming = m.role === "assistant" && i === messages.length - 1;
          return (
            <div key={m.id ?? i} style={{ marginBottom: "0.5rem" }}>
              {/* Chronological order: Show tool invocations first (they run before final text) */}
              {m.toolInvocations?.map((ti) => {
                const isResult = ti.state === "result";
                const toolName = ti.toolName;
                return (
                  <TerminalLine key={ti.toolCallId} dim style={{ marginTop: "0.25rem", paddingLeft: "1rem" }}>
                    <span style={{ color: "var(--accent)", opacity: 0.8 }}>$ [tool] {toolName}</span>
                    {isResult ? (
                      <span style={{ color: "var(--success)", marginLeft: "0.5rem" }}>
                        ✓ success{ti.result && typeof ti.result === "object" && "data" in ti.result && Array.isArray((ti.result as any).data) ? ` (${(ti.result as any).data.length} results)` : ""}
                      </span>
                    ) : (
                      <span className="shukan-prompt-cursor" style={{ marginLeft: "0.5rem" }}>running...</span>
                    )}
                  </TerminalLine>
                );
              })}

              {/* Show text content second */}
              {(text || (m.role === "assistant" && isStreaming)) ? (
                <TerminalLine prompt={m.role === "user"}>
                  <strong style={{ color: m.role === "user" ? "var(--fg)" : "var(--accent)" }}>
                    {m.role === "user" ? "you" : "»"}
                  </strong>
                  <span style={{ marginLeft: "0.5rem" }}>{isStreaming ? typed : text}</span>
                </TerminalLine>
              ) : null}
            </div>
          );
        })}
        {isLoading && messages.length > 0 && messages[messages.length - 1]?.role === "user" ? (
          <div style={{ marginBottom: "0.5rem" }}>
            <TerminalLine>
              <strong style={{ color: "var(--accent)" }}>»</strong>
              <span className="shukan-prompt-cursor" style={{ marginLeft: "0.5rem", color: "var(--fg-dim)" }}>thinking...</span>
            </TerminalLine>
          </div>
        ) : null}
        {error && (
          <div style={{ marginBottom: "0.5rem" }}>
            <TerminalLine danger>
              $ [system] error: {error.message || String(error)}
            </TerminalLine>
          </div>
        )}
      </div>

      {/* Input Form at bottom */}
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        style={{
          display: "flex",
          gap: "0.5rem",
          alignItems: "stretch",
          position: "sticky",
          bottom: 0,
          backgroundColor: "var(--bg)",
          padding: "0.5rem 0",
        }}
      >
        <input
          className="shukan-input"
          value={input}
          onChange={(e) => handleInputChange(e)}
          placeholder="$ ask, plan, reflect..."
          autoFocus
        />
        {isLoading ? (
          <button type="button" className="shukan-btn" onClick={() => stop()}>
            stop
          </button>
        ) : (
          <button type="submit" className="shukan-btn shukan-btn-primary">
            send
          </button>
        )}
      </form>
    </div>
  );
}
